import { useState } from "react";
import Chip from "../../components/Chip";
import EntityPicker from "../../components/EntityPicker";
import FormField from "../../components/FormField";
import TextArea from "../../components/TextArea";
import TextInput from "../../components/TextInput";
import { useArtistsList } from "../../../hooks/useArtistsList";
import type { ArtistMiniInLieuResponse, LieuDto } from "../../../api/lieuxApi";

interface Props {
  value: LieuDto;
  onChange: (next: LieuDto) => void;
  /** Currently attached artists, used to label chips even before they show up in the picker. */
  attachedArtists?: ArtistMiniInLieuResponse[];
}

export default function LieuFormFields({ value, onChange, attachedArtists }: Props) {
  const [artistSearch, setArtistSearch] = useState("");
  const artistsQ = useArtistsList({ search: artistSearch, page: 0, size: 30, sort: "name,asc" });

  const set = <K extends keyof LieuDto>(key: K, v: LieuDto[K]) => onChange({ ...value, [key]: v });
  const setAddress = (patch: Partial<NonNullable<LieuDto["address"]>>) =>
    onChange({ ...value, address: { ...(value.address ?? {}), ...patch } });

  const selectedIds = value.artistIds ?? [];
  const addArtist = (id: string) => {
    if (selectedIds.includes(id)) return;
    set("artistIds", [...selectedIds, id]);
  };
  const removeArtist = (id: string) => set("artistIds", selectedIds.filter((x) => x !== id));

  const chipData: ArtistMiniInLieuResponse[] = selectedIds.map((id) => {
    const fromAttached = (attachedArtists ?? []).find((a) => a.id === id);
    if (fromAttached) return fromAttached;
    const fromCatalog = artistsQ.data?.content.find((a) => a.id === id);
    return { id, name: fromCatalog?.name ?? id };
  });

  const pickerOptions =
    artistsQ.data?.content.map((a) => ({
      id: a.id,
      label: a.name,
      sublabel: a.description ? a.description.slice(0, 60) : "",
    })) ?? [];

  return (
    <div className="bo-stack">
      <FormField label="Nom" required>
        <TextInput value={value.name ?? ""} onChange={(e) => set("name", e.target.value)} required />
      </FormField>

      <FormField label="Description">
        <TextArea
          value={value.description ?? ""}
          onChange={(e) => set("description", e.target.value)}
          rows={4}
        />
      </FormField>

      <FormField
        label="Nom de l'adresse (libellé libre)"
        hint="Ex. Grand-Place, Bruxelles — affiché quand l'adresse structurée n'est pas remplie."
      >
        <TextInput value={value.addressName ?? ""} onChange={(e) => set("addressName", e.target.value)} />
      </FormField>

      <fieldset className="bo-fieldset">
        <legend>Adresse structurée (géocodée automatiquement si le service adresse est configuré)</legend>
        <div className="bo-grid-3">
          <FormField label="Code postal">
            <TextInput
              value={value.address?.postcode ?? ""}
              onChange={(e) => setAddress({ postcode: e.target.value })}
            />
          </FormField>
          <FormField label="Ville">
            <TextInput
              value={value.address?.city ?? ""}
              onChange={(e) => setAddress({ city: e.target.value })}
            />
          </FormField>
          <FormField label="Rue">
            <TextInput
              value={value.address?.street ?? ""}
              onChange={(e) => setAddress({ street: e.target.value })}
            />
          </FormField>
        </div>
      </fieldset>

      <fieldset className="bo-fieldset">
        <legend>Artistes accueillis</legend>
        {chipData.length === 0 ? (
          <p style={{ color: "var(--ink-mute)", fontSize: 13, margin: 0 }}>
            Aucun artiste associé pour le moment.
          </p>
        ) : (
          <div className="bo-chip-list">
            {chipData.map((a) => (
              <Chip key={a.id} label={a.name} onRemove={() => removeArtist(a.id)} />
            ))}
          </div>
        )}
        <div style={{ marginTop: "var(--sp-3)" }}>
          <EntityPicker
            placeholder="Rechercher un artiste…"
            options={pickerOptions}
            excludeIds={selectedIds}
            onSearchChange={setArtistSearch}
            onPick={(o) => addArtist(o.id)}
            loading={artistsQ.isLoading}
          />
        </div>
      </fieldset>
    </div>
  );
}
